import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Zap, AlertCircle, Crown, CheckCircle } from 'lucide-react';

const TrialBanner = ({ trialStatus, onUpgradeClick }) => {
  if (!trialStatus || !trialStatus.trial_active) {
    return null;
  }

  const { custom_voice_trial, full_trial } = trialStatus;

  // Determine urgency level
  const isUrgent = full_trial.days_remaining <= 3 || full_trial.products_remaining <= 2;
  const isExpired = full_trial.expired;

  // Color scheme based on urgency
  const getBannerColor = () => {
    if (isExpired) return 'from-red-500 to-red-600';
    if (isUrgent) return 'from-orange-500 to-red-500';
    return 'from-indigo-500 to-purple-600';
  };

  const getIconColor = () => {
    if (isExpired) return 'text-red-100';
    if (isUrgent) return 'text-orange-100';
    return 'text-indigo-100';
  };

  return (
    <Card className={`bg-gradient-to-r ${getBannerColor()} text-white border-0 shadow-xl mb-8`}>
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              {isExpired ? (
                <AlertCircle className={`h-6 w-6 ${getIconColor()}`} />
              ) : (
                <Zap className={`h-6 w-6 ${getIconColor()}`} />
              )}
              <h3 className="text-xl font-bold">
                {isExpired ? '🚨 Trial Expired' : '⚡ Free Trial Active'}
              </h3>
            </div>

            {isExpired ? (
              <div className="space-y-2">
                <p className="text-white/90 text-base">
                  Your 30-day trial has ended. Upgrade now to continue generating audio and access all features!
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Full Trial Status */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm font-semibold">Full Access</span>
                  </div>
                  <div className="text-2xl font-bold mb-1">
                    {full_trial.days_remaining} days
                  </div>
                  <p className="text-xs text-white/80">
                    Remaining until {new Date(full_trial.expires_at).toLocaleDateString()}
                  </p>
                </div>

                {/* Products Used */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-semibold">Products</span>
                  </div>
                  <div className="text-2xl font-bold mb-1">
                    {full_trial.products_generated}/{full_trial.products_limit}
                  </div>
                  <p className="text-xs text-white/80">
                    {full_trial.products_remaining} products remaining
                  </p>
                  {full_trial.products_remaining <= 2 && (
                    <Badge className="mt-2 bg-orange-500 border-0 text-white">
                      Almost at limit!
                    </Badge>
                  )}
                </div>

                {/* Custom Voice Status */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="h-4 w-4" />
                    <span className="text-sm font-semibold">Custom Voice</span>
                  </div>
                  <div className="text-2xl font-bold mb-1">
                    {custom_voice_trial.custom_voice_created ? '1/1' : '0/1'}
                  </div>
                  <p className="text-xs text-white/80">
                    {custom_voice_trial.expired 
                      ? 'Trial period ended' 
                      : custom_voice_trial.custom_voice_created
                        ? 'Trial voice created'
                        : `${custom_voice_trial.days_remaining} days to create`
                    }
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Upgrade Button */}
          <div className="flex-shrink-0">
            <Button
              onClick={onUpgradeClick}
              variant="secondary"
              className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold"
              size="lg"
            >
              <Crown className="h-4 w-4 mr-2" />
              Upgrade Now
            </Button>
          </div>
        </div>

        {/* Progress Bars */}
        {!isExpired && (
          <div className="mt-6 space-y-3">
            {/* Full Trial Progress */}
            <div>
              <div className="flex items-center justify-between mb-1 text-xs">
                <span className="text-white/80">Trial Period</span>
                <span className="text-white font-semibold">
                  {Math.round((full_trial.days_remaining / 30) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-500"
                  style={{ width: `${(full_trial.days_remaining / 30) * 100}%` }}
                />
              </div>
            </div>

            {/* Products Progress */}
            <div>
              <div className="flex items-center justify-between mb-1 text-xs">
                <span className="text-white/80">Product Limit</span>
                <span className="text-white font-semibold">
                  {Math.round((full_trial.products_remaining / full_trial.products_limit) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    full_trial.products_remaining <= 2 ? 'bg-orange-400' : 'bg-white'
                  }`}
                  style={{
                    width: `${(full_trial.products_remaining / full_trial.products_limit) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default TrialBanner;
